import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, MapPin, Pencil, Trash2, FolderOpen, Share2, FolderPlus, LayoutGrid, List } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useEscapeToHome } from '@/hooks/useKeyboard';

export default function ProjectsPage() {
  const { projects, fetchProjects } = useApp();
  useEscapeToHome();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', location: '', description: '', parent_project_id: null });
  const [loading, setLoading] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [shareUsername, setShareUsername] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [viewMode, setViewMode] = useState('list'); // list | card
  const [selected, setSelected] = useState(new Set());
  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const selectAllToggle = () => {
    const all = projects.map(p => p.id);
    if (all.every(id => selected.has(id))) setSelected(new Set());
    else setSelected(new Set(all));
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} workspace(s)?`)) return;
    try {
      const { data } = await api.post('/bulk-delete-items', { items: Array.from(selected).map(id => ({ type: 'project', id })) });
      toast.success(data.message); setSelected(new Set()); fetchProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openCreate = () => { setEditing(null); setForm({ name: '', location: '', description: '', parent_project_id: null }); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, location: p.location || '', description: p.description || '', parent_project_id: p.parent_project_id || null }); setShowForm(true); };
  const openCreateSub = (parent) => { setEditing(null); setForm({ name: '', location: '', description: '', parent_project_id: parent.id }); setShowForm(true); };

  // 'N' for new, 'V' for toggle view
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'n' && !isTyping) { e.preventDefault(); openCreate(); }
      else if (e.key === 'v' && !isTyping) { e.preventDefault(); setViewMode(m => m === 'card' ? 'list' : 'card'); }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  });

  const handleFormKeyDown = (e, fieldIndex, totalFields) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (fieldIndex >= totalFields - 1) {
        save();
      } else {
        const allFields = Array.from(document.querySelectorAll('[data-form-idx]'))
          .sort((a, b) => Number(a.getAttribute('data-form-idx')) - Number(b.getAttribute('data-form-idx')));
        const currentIdx = allFields.findIndex(el => Number(el.getAttribute('data-form-idx')) === fieldIndex);
        if (currentIdx >= 0 && currentIdx < allFields.length - 1) {
          const next = allFields[currentIdx + 1];
          next.focus();
        }
      }
    }
  };

  const save = async () => {
    if (!form.name) { toast.error('Workspace name is required'); return; }
    setLoading(true);
    try {
      if (editing) { await api.put(`/projects/${editing.id}`, form); toast.success('Workspace updated'); }
      else { await api.post('/projects', form); toast.success('Workspace created'); }
      setShowForm(false); fetchProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast('Workspace moved to trash', { action: { label: 'Undo', onClick: async () => { await api.post(`/restore/project/${id}`); fetchProjects(); } } });
      fetchProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const shareWorkspace = async () => {
    if (!shareUsername) { toast.error('Enter a username'); return; }
    try {
      await api.post('/share-workspace', { workspace_id: showShare.id, shared_with_username: shareUsername, permission: sharePermission });
      toast.success(`Workspace shared with @${shareUsername}`);
      setShowShare(null); setShareUsername('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="workspaces-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Workspaces</h2>
            <p className="text-sm text-zinc-500 mt-1">Organize your work into workspaces</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Select All */}
            {projects.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer mr-2" data-testid="select-all-workspaces">
                <Checkbox checked={projects.length > 0 && projects.every(p => selected.has(p.id))} onCheckedChange={selectAllToggle} className="h-3.5 w-3.5" />
                <span>All</span>
              </label>
            )}
            <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
              <button onClick={() => setViewMode('card')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'card' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="workspaces-view-card">
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Card</button>
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="workspaces-view-list">
                <List className="h-3.5 w-3.5" strokeWidth={1.5} /> List</button>
            </div>
            <Button onClick={openCreate} className="rounded-xl" data-testid="create-workspace-btn">
              <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New Workspace
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:px-8">
        <div className="w-full">

      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <img src="https://static.prod-images.emergentagent.com/jobs/89959381-8fb5-45c9-9dbb-0dfb477e8d4d/images/3aef46e34a35980c9bb9d820a9a836a3c330b826e071b83d88fbfb4794886dd4.png"
            alt="Empty" className="w-48 h-48 opacity-50 mb-6" />
          <p className="text-zinc-500 text-sm">No workspaces yet. Create your first workspace.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(() => {
            const topLevel = projects.filter(p => !p.parent_project_id);
            const subsByParent = {};
            projects.forEach(p => { if (p.parent_project_id) { (subsByParent[p.parent_project_id] = subsByParent[p.parent_project_id] || []).push(p); } });
            return topLevel.map(p => (
              <div key={p.id}>
                <div className="flex items-center justify-between p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:shadow-md transition-all group"
                  data-testid={`workspace-item-${p.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} className="h-4 w-4 shrink-0" data-testid={`ws-select-${p.id}`} />
                    <div className="w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
                      <FolderOpen className="h-5 w-5 text-zinc-500" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium truncate">{p.name}</h3>
                      <div className="flex items-center gap-3 mt-0.5">
                        {p.location && <p className="text-xs text-zinc-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</p>}
                        {(subsByParent[p.id] || []).length > 0 && <p className="text-xs text-zinc-400">{(subsByParent[p.id]||[]).length} sub-workspace{(subsByParent[p.id]||[]).length !== 1 ? 's' : ''}</p>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openCreateSub(p)} data-testid={`add-sub-workspace-${p.id}`}>
                      <FolderPlus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Sub-Workspace
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setShowShare(p); setShareUsername(''); setSharePermission('view'); }} data-testid={`share-workspace-${p.id}`}>
                      <Share2 className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-workspace-${p.id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteProject(p.id)} data-testid={`delete-workspace-${p.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                {/* Sub-workspaces */}
                {(subsByParent[p.id] || []).map(sub => (
                  <div key={sub.id} className="flex items-center justify-between p-3 pl-12 my-1 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:shadow-sm transition-all group"
                    data-testid={`sub-workspace-item-${sub.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Checkbox checked={selected.has(sub.id)} onCheckedChange={() => toggleSelect(sub.id)} className="h-3.5 w-3.5 shrink-0" data-testid={`ws-select-${sub.id}`} />
                      <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
                        <FolderOpen className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm truncate">{sub.name}</h4>
                        {sub.description && <p className="text-xs text-zinc-400 truncate">{sub.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(sub)} data-testid={`edit-sub-workspace-${sub.id}`}>
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteProject(sub.id)} data-testid={`delete-sub-workspace-${sub.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      )}
      </div>
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 bg-zinc-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50" data-testid="workspaces-selection-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs underline opacity-70" data-testid="workspaces-clear-selection">Clear</button>
          <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="workspaces-bulk-delete"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
      )}

      {/* Workspace Form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="workspace-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Workspace' : (form.parent_project_id ? 'New Sub-Workspace' : 'New Workspace')}</DialogTitle>
            <DialogDescription>
              {form.parent_project_id && !editing ? `Creating under "${projects.find(pp => pp.id === form.parent_project_id)?.name || ''}"` : (editing ? 'Update your workspace details' : 'Create a new workspace to organize your work')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Home, Business, Factory" className="h-12" data-testid="workspace-name-input" data-form-idx="0" tabIndex={0}
                onKeyDown={e => handleFormKeyDown(e, 0, 3)} />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                placeholder="e.g., New York" className="h-12" data-testid="workspace-location-input" data-form-idx="1" tabIndex={0}
                onKeyDown={e => handleFormKeyDown(e, 1, 3)} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description" className="h-12" data-testid="workspace-description-input" data-form-idx="2" tabIndex={0}
                onKeyDown={e => handleFormKeyDown(e, 2, 3)} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={loading} className="rounded-xl" data-testid="workspace-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Workspace Dialog */}
      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent data-testid="share-workspace-dialog">
          <DialogHeader>
            <DialogTitle>Share Workspace</DialogTitle>
            <DialogDescription>Share all records in "{showShare?.name}" with another user</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={shareUsername} onChange={e => setShareUsername(e.target.value)}
                placeholder="Enter username" data-testid="share-ws-username"
                onKeyDown={e => { if (e.key === 'Enter') shareWorkspace(); }} />
            </div>
            <div className="space-y-2">
              <Label>Permission</Label>
              <Select value={sharePermission} onValueChange={setSharePermission}>
                <SelectTrigger data-testid="share-ws-permission"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View</SelectItem>
                  <SelectItem value="edit">Edit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={shareWorkspace} className="rounded-xl" data-testid="share-ws-submit">Share Workspace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
